import {
  type CommonZoneLayout,
  cloneMapLayout,
  type DoorWall,
  type MapFurniture,
  type MapFurnitureKind,
  type MapLayoutDocument,
  type MapTransition,
  makePodFromBounds,
  type PersonalOfficeSlotLayout,
} from "@/lib/office/personal-office-layout";
import { furniturePlacementDefaults } from "@/lib/office/sprites";

/** Single selectable object on the canvas. */
export type EditorItemRef =
  | { kind: "office"; id: string }
  | { kind: "zone"; id: string }
  | { kind: "furniture"; id: string }
  | { kind: "spawn"; index: number }
  | { kind: "transition"; id: string };

/** @deprecated Prefer EditorItemRef | null; kept for call-site clarity. */
export type EditorSelection = EditorItemRef | null;

export type EditorSelectionSet = EditorItemRef[];

export type OutdoorZoneTemplate =
  | "path"
  | "cobble_round"
  | "cobble_sand"
  | "brick"
  | "brick_dark"
  | "sidewalk"
  | "pavement"
  | "dirt"
  | "deck"
  | "park";

export type PaletteItem =
  | { type: "office" }
  | {
      type: "zone";
      zoneTemplate: "meeting" | "huddle" | "lounge" | "focus" | OutdoorZoneTemplate;
    }
  | { type: "furniture"; furnitureKind: MapFurnitureKind }
  | {
      type: "building";
      style: string;
      label: string;
      color: string;
      w: number;
      d: number;
      toMapId: string;
    }
  | { type: "transition"; toMapId: string; label: string }
  | { type: "spawn" };

/** Campus outdoor building presets for the map designer palette. */
/**
 * Inclusive solid shell (matches BUILDING_SHELL_SIZE). Door is the mid tile on
 * the south face of the mass (force-walked); remaining mass tiles are blocked.
 */
export const CAMPUS_BUILDING_PRESETS: Array<{
  style: string;
  label: string;
  color: string;
  w: number;
  d: number;
  toMapId: string;
}> = [
  { style: "cafe", label: "Cafe", color: "#8B5A2B", w: 6, d: 3, toMapId: "cafe" },
  {
    style: "people_marketing",
    label: "People + Marketing",
    color: "#7A7A88",
    w: 5,
    d: 3,
    toMapId: "people_marketing",
  },
  { style: "reception", label: "Reception", color: "#4A7AB8", w: 5, d: 3, toMapId: "reception" },
  { style: "delivery", label: "Delivery", color: "#3A6AA8", w: 6, d: 3, toMapId: "delivery" },
  {
    style: "post_delivery",
    label: "Post Delivery",
    color: "#D8D0C4",
    w: 5,
    d: 3,
    toMapId: "post_delivery",
  },
  {
    style: "customer_success",
    label: "Customer Success",
    color: "#3A9A5A",
    w: 6,
    d: 3,
    toMapId: "customer_success",
  },
];

export const CELL = 14;

/** Zone template default footprints (inclusive width = w+1 in x2-x1 terms). */
export const ZONE_TEMPLATE_SPECS: Record<
  "meeting" | "huddle" | "lounge" | "focus" | OutdoorZoneTemplate,
  {
    w: number;
    h: number;
    name: string;
    priv?: boolean;
    roomKind?: "meeting" | "huddle";
    rug: string;
  }
> = {
  meeting: {
    w: 12,
    h: 8,
    name: "Company Meeting",
    priv: true,
    roomKind: "meeting",
    rug: "#EAF1F9",
  },
  huddle: { w: 6, h: 6, name: "Huddle", priv: true, roomKind: "huddle", rug: "#EDE8F5" },
  lounge: { w: 10, h: 7, name: "The Lounge", rug: "#E4EEF2" },
  focus: { w: 10, h: 7, name: "Focus Zone", rug: "#E9E8F3" },
  path: { w: 12, h: 4, name: "Cobble path", rug: "#B8BCC6" },
  cobble_round: { w: 10, h: 4, name: "Round cobbles", rug: "#9CA3B0" },
  cobble_sand: { w: 10, h: 4, name: "Sandstone cobbles", rug: "#C4B8A0" },
  brick: { w: 10, h: 4, name: "Red brick", rug: "#B85A42" },
  brick_dark: { w: 10, h: 4, name: "Dark brick", rug: "#6B3A2E" },
  sidewalk: { w: 10, h: 4, name: "Sidewalk", rug: "#D0D4DC" },
  pavement: { w: 10, h: 6, name: "Pavement", rug: "#A8ACB8" },
  dirt: { w: 8, h: 4, name: "Dirt path", rug: "#8B6F47" },
  deck: { w: 8, h: 6, name: "Wood deck", rug: "#C4A574" },
  park: { w: 12, h: 10, name: "Park grass", rug: "#C6D96A" },
};

/** Validator requires transition ids: ^[a-z][a-z0-9_-]*$ (UUIDs often start with a digit). */
function newSnakeId(prefix: string): string {
  const raw = crypto.randomUUID().replace(/-/g, "").toLowerCase();
  const safe = prefix
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/^[^a-z]+/, "x");
  return `${safe}_${raw.slice(0, 10)}`;
}

function nextOfficeSlotKey(offices: PersonalOfficeSlotLayout[]): string {
  let max = 0;
  for (const o of offices) {
    const m = /^office_(\d+)$/.exec(o.slotKey);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `office_${max + 1}`;
}

function nextZoneId(zones: CommonZoneLayout[], prefix: string): string {
  const ids = new Set(zones.map((z) => z.id));
  // Bare prefix if free (e.g. first "meeting" zone → "meeting").
  if (!ids.has(prefix)) return prefix;
  // Otherwise the first free "<prefix>_<n>". The taken-prefix check must NOT be in
  // the loop condition — `ids.has(prefix)` never flips as n grows, so leaving it
  // there spins forever whenever a zone's id already equals the prefix.
  let n = 2;
  while (ids.has(`${prefix}_${n}`)) n += 1;
  return `${prefix}_${n}`;
}

export function itemRefKey(ref: EditorItemRef): string {
  if (ref.kind === "spawn") return `spawn:${ref.index}`;
  return `${ref.kind}:${ref.id}`;
}

export function sameItemRef(a: EditorItemRef, b: EditorItemRef): boolean {
  return itemRefKey(a) === itemRefKey(b);
}

export function isSelected(selection: EditorSelectionSet, ref: EditorItemRef): boolean {
  return selection.some((s) => sameItemRef(s, ref));
}

export function primarySelection(selection: EditorSelectionSet): EditorItemRef | null {
  return selection.length > 0 ? (selection[selection.length - 1] ?? null) : null;
}

/**
 * Building placement links mass + door + transition.
 * - Editor places: `bldg_style_suffix`, `door_style_suffix`, `to_map_suffix`
 * - Seeded campus: `bldg-style-mass`, `bldg-style-door`, transition by style/toMapId
 */
export function expandBuildingGroup(
  layout: MapLayoutDocument,
  refs: EditorSelectionSet,
): EditorSelectionSet {
  const out: EditorItemRef[] = [];
  const seen = new Set<string>();
  const add = (ref: EditorItemRef) => {
    const k = itemRefKey(ref);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(ref);
  };

  for (const ref of refs) {
    add(ref);
    if (ref.kind !== "furniture") continue;
    const f = layout.furniture.find((x) => x.id === ref.id);
    if (!f || (f.kind !== "building" && f.kind !== "door")) continue;

    // Pattern A: editor place — bldg_style_random / door_style_random
    const snake = /^bldg_(.+)_(.+)$/.exec(f.id) ?? /^door_(.+)_(.+)$/.exec(f.id);
    if (snake?.[2]) {
      const suffix = snake[2];
      for (const piece of layout.furniture) {
        if (piece.id === f.id) continue;
        if (
          piece.id.endsWith(`_${suffix}`) &&
          (piece.kind === "door" || piece.kind === "building")
        ) {
          add({ kind: "furniture", id: piece.id });
        }
      }
      for (const t of layout.transitions ?? []) {
        if (t.id.endsWith(`_${suffix}`)) {
          add({ kind: "transition", id: t.id });
        }
      }
      continue;
    }

    // Pattern B: seeded campus — bldg-style-mass / bldg-style-door
    const kebab = /^bldg-([a-z0-9_]+)-(mass|door)$/.exec(f.id);
    const style = f.style ?? kebab?.[1];
    if (!style) continue;
    for (const piece of layout.furniture) {
      if (piece.id === f.id) continue;
      if (piece.style === style && (piece.kind === "door" || piece.kind === "building")) {
        add({ kind: "furniture", id: piece.id });
      }
      // legacy ids without style field
      if (
        piece.id === `bldg-${style}-mass` ||
        piece.id === `bldg-${style}-door` ||
        piece.id === `door_${style}`
      ) {
        add({ kind: "furniture", id: piece.id });
      }
    }
    for (const t of layout.transitions ?? []) {
      if (
        t.id === `campus-to-${style}` ||
        t.id === `to_${style}` ||
        t.toMapId === style ||
        (f.displayName && t.label === f.displayName)
      ) {
        // Only attach transition if it sits on/near this building footprint
        const bounds = getItemBounds(layout, { kind: "furniture", id: f.id });
        if (!bounds) {
          add({ kind: "transition", id: t.id });
          continue;
        }
        const near =
          t.gx >= bounds.x1 - 1 &&
          t.gx <= bounds.x2 + 1 &&
          t.gy >= bounds.y1 - 1 &&
          t.gy <= bounds.y2 + 1;
        if (near) add({ kind: "transition", id: t.id });
      }
    }
  }
  return out;
}

export function placeOffice(
  layout: MapLayoutDocument,
  x1: number,
  y1: number,
  doorWall: DoorWall = "S",
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const size = 4; // 5×5 inclusive
  const slotKey = nextOfficeSlotKey(next.offices);
  const index = Number(slotKey.replace("office_", "")) || next.offices.length + 1;
  next.offices.push(
    makePodFromBounds({
      slotKey,
      nameDefault: `Office ${index}`,
      x1,
      y1,
      x2: x1 + size,
      y2: y1 + size,
      doorWall,
    }),
  );
  return next;
}

export function placeZone(
  layout: MapLayoutDocument,
  template: "meeting" | "huddle" | "lounge" | "focus" | OutdoorZoneTemplate,
  x1: number,
  y1: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const s = ZONE_TEMPLATE_SPECS[template];
  const id = nextZoneId(next.commonZones, template);
  next.commonZones.push({
    id,
    name: s.name,
    x1,
    y1,
    x2: x1 + s.w,
    y2: y1 + s.h,
    rug: s.rug,
    priv: s.priv,
    roomKind: s.roomKind,
  });
  return next;
}

export function placeFurniture(
  layout: MapLayoutDocument,
  kind: MapFurnitureKind,
  gx: number,
  gy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const defaults = furniturePlacementDefaults(kind);
  const item: MapFurniture = {
    id: newSnakeId(kind),
    kind,
    gx,
    gy,
    w: defaults.w,
    d: defaults.d,
    dir: "S",
    seat: defaults.seat,
    block: defaults.block,
  };
  next.furniture.push(item);
  return next;
}

/**
 * Place an outdoor building shell (solid mass + walkable door hole + map transition).
 * Door is mid-tile on the door-face of the mass (force-walked over the blocked body).
 */
export function placeOutdoorBuilding(
  layout: MapLayoutDocument,
  opts: {
    style: string;
    label: string;
    color: string;
    w: number;
    d: number;
    toMapId: string;
    gx: number;
    gy: number;
    doorWall?: DoorWall;
  },
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const doorWall = opts.doorWall ?? "S";
  const w = Math.max(3, opts.w);
  const d = Math.max(2, opts.d);
  // Clamp so the full footprint stays on the map
  const gx = Math.max(0, Math.min(opts.gx, next.cols - w));
  const gy = Math.max(0, Math.min(opts.gy, next.rows - d));
  const suffix = newSnakeId(opts.style).split("_").pop() ?? "x";

  // Door is a walkable hole on the face of the solid mass (not outside it)
  const midX = gx + Math.floor(w / 2);
  const midY = gy + Math.floor(d / 2);
  let doorGx = midX;
  let doorGy = midY;
  if (doorWall === "S") doorGy = gy + d - 1;
  else if (doorWall === "N") doorGy = gy;
  else if (doorWall === "E") doorGx = gx + w - 1;
  else doorGx = gx;

  const mass: MapFurniture = {
    id: `bldg_${opts.style}_${suffix}`,
    kind: "building",
    gx,
    gy,
    w,
    d,
    dir: "S",
    block: true,
    c: opts.color,
    displayName: opts.label,
    doorWall,
    style: opts.style,
  };
  const door: MapFurniture = {
    id: `door_${opts.style}_${suffix}`,
    kind: "door",
    gx: doorGx,
    gy: doorGy,
    w: 1,
    d: 1,
    dir: "S",
    displayName: opts.label,
    style: opts.style,
  };
  next.furniture.push(mass, door);

  // Transition on the door tile → indoor lobby
  const transitions = [...(next.transitions ?? [])];
  transitions.push({
    id: `to_${opts.toMapId}_${suffix}`,
    gx: doorGx,
    gy: doorGy,
    toMapId: opts.toMapId,
    toSpawn: { gx: 27, gy: 18 },
    label: opts.label,
  });
  next.transitions = transitions;

  return next;
}

export function placeSpawn(layout: MapLayoutDocument, gx: number, gy: number): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.spawnPoints.push({ gx, gy });
  return next;
}

export function placeTransition(
  layout: MapLayoutDocument,
  gx: number,
  gy: number,
  toMapId: string,
  label: string,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const transitions = [...(next.transitions ?? [])];
  transitions.push({
    id: newSnakeId(`to_${toMapId}`),
    gx,
    gy,
    toMapId,
    toSpawn: { gx: 27, gy: 18 },
    label,
  });
  next.transitions = transitions;
  return next;
}

export function deleteSelection(
  layout: MapLayoutDocument,
  selection: EditorSelection,
): MapLayoutDocument {
  if (!selection) return layout;
  return deleteItems(layout, [selection]);
}

export function deleteItems(
  layout: MapLayoutDocument,
  refs: EditorSelectionSet,
): MapLayoutDocument {
  if (refs.length === 0) return layout;
  const expanded = expandBuildingGroup(layout, refs);
  const next = cloneMapLayout(layout);

  const officeIds = new Set(expanded.filter((r) => r.kind === "office").map((r) => r.id));
  const zoneIds = new Set(expanded.filter((r) => r.kind === "zone").map((r) => r.id));
  const furnIds = new Set(expanded.filter((r) => r.kind === "furniture").map((r) => r.id));
  const transIds = new Set(expanded.filter((r) => r.kind === "transition").map((r) => r.id));
  const spawnIndices = new Set(
    expanded
      .filter((r): r is { kind: "spawn"; index: number } => r.kind === "spawn")
      .map((r) => r.index),
  );

  if (officeIds.size > 0) {
    next.offices = next.offices.filter((o) => !officeIds.has(o.slotKey));
  }
  if (zoneIds.size > 0) {
    next.commonZones = next.commonZones.filter((z) => !zoneIds.has(z.id));
  }
  if (furnIds.size > 0) {
    next.furniture = next.furniture.filter((f) => !furnIds.has(f.id));
  }
  if (transIds.size > 0) {
    next.transitions = (next.transitions ?? []).filter((t) => !transIds.has(t.id));
  }
  if (spawnIndices.size > 0) {
    next.spawnPoints = next.spawnPoints.filter((_, i) => !spawnIndices.has(i));
  }
  return next;
}

export function moveOffice(
  layout: MapLayoutDocument,
  slotKey: string,
  dx: number,
  dy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const o = next.offices.find((x) => x.slotKey === slotKey);
  if (!o) return layout;
  return {
    ...next,
    offices: next.offices.map((office) => {
      if (office.slotKey !== slotKey) return office;
      return makePodFromBounds({
        slotKey: office.slotKey,
        nameDefault: office.nameDefault,
        x1: office.x1 + dx,
        y1: office.y1 + dy,
        x2: office.x2 + dx,
        y2: office.y2 + dy,
        doorWall: office.doorWall,
      });
    }),
  };
}

export function moveZone(
  layout: MapLayoutDocument,
  id: string,
  dx: number,
  dy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.commonZones = next.commonZones.map((z) =>
    z.id === id ? { ...z, x1: z.x1 + dx, y1: z.y1 + dy, x2: z.x2 + dx, y2: z.y2 + dy } : z,
  );
  return next;
}

export function moveFurniture(
  layout: MapLayoutDocument,
  id: string,
  dx: number,
  dy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.furniture = next.furniture.map((f) =>
    f.id === id ? { ...f, gx: f.gx + dx, gy: f.gy + dy } : f,
  );
  return next;
}

export function moveTransition(
  layout: MapLayoutDocument,
  id: string,
  dx: number,
  dy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.transitions = (next.transitions ?? []).map((t) =>
    t.id === id ? { ...t, gx: t.gx + dx, gy: t.gy + dy } : t,
  );
  return next;
}

export function moveSpawn(
  layout: MapLayoutDocument,
  index: number,
  dx: number,
  dy: number,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  const p = next.spawnPoints[index];
  if (!p) return layout;
  next.spawnPoints = next.spawnPoints.map((s, i) =>
    i === index ? { gx: s.gx + dx, gy: s.gy + dy } : s,
  );
  return next;
}

/** Move many items by the same grid delta (one clone pass). Expands building groups. */
export function moveItems(
  layout: MapLayoutDocument,
  refs: EditorSelectionSet,
  dx: number,
  dy: number,
): MapLayoutDocument {
  if ((dx === 0 && dy === 0) || refs.length === 0) return layout;
  const expanded = expandBuildingGroup(layout, refs);
  const next = cloneMapLayout(layout);

  const officeIds = new Set(expanded.filter((r) => r.kind === "office").map((r) => r.id));
  const zoneIds = new Set(expanded.filter((r) => r.kind === "zone").map((r) => r.id));
  const furnIds = new Set(expanded.filter((r) => r.kind === "furniture").map((r) => r.id));
  const transIds = new Set(expanded.filter((r) => r.kind === "transition").map((r) => r.id));
  const spawnIndices = new Set(
    expanded
      .filter((r): r is { kind: "spawn"; index: number } => r.kind === "spawn")
      .map((r) => r.index),
  );

  if (officeIds.size > 0) {
    next.offices = next.offices.map((office) => {
      if (!officeIds.has(office.slotKey)) return office;
      return makePodFromBounds({
        slotKey: office.slotKey,
        nameDefault: office.nameDefault,
        x1: office.x1 + dx,
        y1: office.y1 + dy,
        x2: office.x2 + dx,
        y2: office.y2 + dy,
        doorWall: office.doorWall,
      });
    });
  }
  if (zoneIds.size > 0) {
    next.commonZones = next.commonZones.map((z) =>
      zoneIds.has(z.id) ? { ...z, x1: z.x1 + dx, y1: z.y1 + dy, x2: z.x2 + dx, y2: z.y2 + dy } : z,
    );
  }
  if (furnIds.size > 0) {
    next.furniture = next.furniture.map((f) =>
      furnIds.has(f.id) ? { ...f, gx: f.gx + dx, gy: f.gy + dy } : f,
    );
  }
  if (transIds.size > 0) {
    next.transitions = (next.transitions ?? []).map((t) =>
      transIds.has(t.id) ? { ...t, gx: t.gx + dx, gy: t.gy + dy } : t,
    );
  }
  if (spawnIndices.size > 0) {
    next.spawnPoints = next.spawnPoints.map((s, i) =>
      spawnIndices.has(i) ? { gx: s.gx + dx, gy: s.gy + dy } : s,
    );
  }
  return next;
}

function rectsIntersect(
  a: { x1: number; y1: number; x2: number; y2: number },
  b: { x1: number; y1: number; x2: number; y2: number },
): boolean {
  return !(a.x2 < b.x1 || b.x2 < a.x1 || a.y2 < b.y1 || b.y2 < a.y1);
}

/** All items whose footprint intersects the inclusive tile rect (marquee). */
export function itemsInRect(
  layout: MapLayoutDocument,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): EditorSelectionSet {
  const rx1 = Math.min(x1, x2);
  const ry1 = Math.min(y1, y2);
  const rx2 = Math.max(x1, x2);
  const ry2 = Math.max(y1, y2);
  const box = { x1: rx1, y1: ry1, x2: rx2, y2: ry2 };
  const out: EditorItemRef[] = [];

  for (const z of layout.commonZones) {
    if (rectsIntersect(box, { x1: z.x1, y1: z.y1, x2: z.x2, y2: z.y2 })) {
      out.push({ kind: "zone", id: z.id });
    }
  }
  for (const o of layout.offices) {
    if (rectsIntersect(box, { x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2 })) {
      out.push({ kind: "office", id: o.slotKey });
    }
  }
  for (const f of layout.furniture) {
    const fx2 = f.gx + f.w - 1;
    const fy2 = f.gy + f.d - 1;
    if (rectsIntersect(box, { x1: f.gx, y1: f.gy, x2: fx2, y2: fy2 })) {
      out.push({ kind: "furniture", id: f.id });
    }
  }
  for (const t of layout.transitions ?? []) {
    if (rectsIntersect(box, { x1: t.gx, y1: t.gy, x2: t.gx, y2: t.gy })) {
      out.push({ kind: "transition", id: t.id });
    }
  }
  layout.spawnPoints.forEach((p, index) => {
    if (rectsIntersect(box, { x1: p.gx, y1: p.gy, x2: p.gx, y2: p.gy })) {
      out.push({ kind: "spawn", index });
    }
  });
  return out;
}

/** Duplicate selected items offset by (+1,+1) with new ids. Returns { layout, selection }. */
export function duplicateItems(
  layout: MapLayoutDocument,
  refs: EditorSelectionSet,
): { layout: MapLayoutDocument; selection: EditorSelectionSet } {
  if (refs.length === 0) return { layout, selection: refs };
  // Don't expand buildings for duplicate of only door — only when building mass is selected.
  const expanded = expandBuildingGroup(layout, refs);
  const next = cloneMapLayout(layout);
  const newSelection: EditorItemRef[] = [];
  const dx = 1;
  const dy = 1;

  // Spawns first (index-based) — append so indices of originals stay stable
  const spawnRefs = expanded
    .filter((r): r is { kind: "spawn"; index: number } => r.kind === "spawn")
    .sort((a, b) => a.index - b.index);
  for (const r of spawnRefs) {
    const p = layout.spawnPoints[r.index];
    if (!p) continue;
    next.spawnPoints.push({ gx: p.gx + dx, gy: p.gy + dy });
    newSelection.push({ kind: "spawn", index: next.spawnPoints.length - 1 });
  }

  for (const r of expanded) {
    if (r.kind === "spawn") continue;
    if (r.kind === "office") {
      const o = layout.offices.find((x) => x.slotKey === r.id);
      if (!o) continue;
      const slotKey = nextOfficeSlotKey(next.offices);
      const index = Number(slotKey.replace("office_", "")) || next.offices.length + 1;
      next.offices.push(
        makePodFromBounds({
          slotKey,
          nameDefault: `${o.nameDefault} copy`,
          x1: o.x1 + dx,
          y1: o.y1 + dy,
          x2: o.x2 + dx,
          y2: o.y2 + dy,
          doorWall: o.doorWall,
        }),
      );
      newSelection.push({ kind: "office", id: slotKey });
      void index;
    } else if (r.kind === "zone") {
      const z = layout.commonZones.find((x) => x.id === r.id);
      if (!z) continue;
      const id = nextZoneId(next.commonZones, z.id.replace(/_\d+$/, "") || "zone");
      next.commonZones.push({
        ...z,
        id,
        name: `${z.name} copy`,
        x1: z.x1 + dx,
        y1: z.y1 + dy,
        x2: z.x2 + dx,
        y2: z.y2 + dy,
      });
      newSelection.push({ kind: "zone", id });
    } else if (r.kind === "furniture") {
      const f = layout.furniture.find((x) => x.id === r.id);
      if (!f) continue;
      const id = newSnakeId(f.kind);
      next.furniture.push({
        ...f,
        id,
        gx: f.gx + dx,
        gy: f.gy + dy,
      });
      newSelection.push({ kind: "furniture", id });
    } else if (r.kind === "transition") {
      const t = (layout.transitions ?? []).find((x) => x.id === r.id);
      if (!t) continue;
      const id = newSnakeId(`to_${t.toMapId}`);
      const transitions = [...(next.transitions ?? [])];
      transitions.push({
        ...t,
        id,
        gx: t.gx + dx,
        gy: t.gy + dy,
        toSpawn: { ...t.toSpawn },
      });
      next.transitions = transitions;
      newSelection.push({ kind: "transition", id });
    }
  }

  return { layout: next, selection: newSelection };
}

/** Placement ghost footprint for palette hover (inclusive tile counts as w/h). */
export function paletteGhostSize(palette: PaletteItem): { w: number; h: number } {
  if (palette.type === "office") return { w: 5, h: 5 };
  if (palette.type === "zone") {
    const s = ZONE_TEMPLATE_SPECS[palette.zoneTemplate];
    return { w: s.w + 1, h: s.h + 1 };
  }
  if (palette.type === "furniture") {
    const d = furniturePlacementDefaults(palette.furnitureKind);
    return { w: d.w, h: d.d };
  }
  if (palette.type === "building") return { w: palette.w, h: palette.d };
  return { w: 1, h: 1 };
}

export function updateOffice(
  layout: MapLayoutDocument,
  slotKey: string,
  patch: Partial<
    Pick<PersonalOfficeSlotLayout, "nameDefault" | "doorWall" | "x1" | "y1" | "x2" | "y2">
  >,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.offices = next.offices.map((o) => {
    if (o.slotKey !== slotKey) return o;
    const x1 = patch.x1 ?? o.x1;
    const y1 = patch.y1 ?? o.y1;
    const x2 = patch.x2 ?? o.x2;
    const y2 = patch.y2 ?? o.y2;
    const doorWall = patch.doorWall ?? o.doorWall;
    return makePodFromBounds({
      slotKey: o.slotKey,
      nameDefault: patch.nameDefault ?? o.nameDefault,
      x1,
      y1,
      x2,
      y2,
      doorWall,
    });
  });
  return next;
}

export function updateZone(
  layout: MapLayoutDocument,
  id: string,
  patch: Partial<CommonZoneLayout>,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.commonZones = next.commonZones.map((z) => (z.id === id ? { ...z, ...patch } : z));
  return next;
}

export function updateFurniture(
  layout: MapLayoutDocument,
  id: string,
  patch: Partial<
    Pick<
      MapFurniture,
      | "gx"
      | "gy"
      | "w"
      | "d"
      | "dir"
      | "seat"
      | "block"
      | "c"
      | "displayName"
      | "doorWall"
      | "style"
    >
  >,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.furniture = next.furniture.map((f) => {
    if (f.id !== id) return f;
    const updated: MapFurniture = { ...f, ...patch };
    if (patch.w !== undefined) updated.w = Math.max(1, Math.min(16, Math.floor(patch.w)));
    if (patch.d !== undefined) updated.d = Math.max(1, Math.min(16, Math.floor(patch.d)));
    if (patch.gx !== undefined) updated.gx = Math.floor(patch.gx);
    if (patch.gy !== undefined) updated.gy = Math.floor(patch.gy);
    return updated;
  });
  return next;
}

export function updateTransition(
  layout: MapLayoutDocument,
  id: string,
  patch: Partial<MapTransition>,
): MapLayoutDocument {
  const next = cloneMapLayout(layout);
  next.transitions = (next.transitions ?? []).map((t) => {
    if (t.id !== id) return t;
    return {
      ...t,
      ...patch,
      toSpawn: patch.toSpawn ? { ...patch.toSpawn } : t.toSpawn,
    };
  });
  return next;
}

/** Resize SE corner of a single office/zone/furniture to inclusive grid coords. */
export function resizeItemSe(
  layout: MapLayoutDocument,
  ref: EditorItemRef,
  endGx: number,
  endGy: number,
): MapLayoutDocument {
  if (ref.kind === "office") {
    const o = layout.offices.find((x) => x.slotKey === ref.id);
    if (!o) return layout;
    const x2 = Math.max(o.x1 + 4, endGx); // min 5×5 inclusive
    const y2 = Math.max(o.y1 + 4, endGy);
    return updateOffice(layout, ref.id, { x2, y2 });
  }
  if (ref.kind === "zone") {
    const z = layout.commonZones.find((x) => x.id === ref.id);
    if (!z) return layout;
    const minSpan = z.roomKind === "meeting" ? 5 : z.roomKind === "huddle" ? 3 : 2;
    const x2 = Math.max(z.x1 + minSpan, endGx);
    const y2 = Math.max(z.y1 + minSpan, endGy);
    return updateZone(layout, ref.id, { x2, y2 });
  }
  if (ref.kind === "furniture") {
    const f = layout.furniture.find((x) => x.id === ref.id);
    if (!f) return layout;
    const w = Math.max(1, Math.min(16, endGx - f.gx + 1));
    const d = Math.max(1, Math.min(16, endGy - f.gy + 1));
    return updateFurniture(layout, ref.id, { w, d });
  }
  return layout;
}

export function getItemBounds(
  layout: MapLayoutDocument,
  ref: EditorItemRef,
): { x1: number; y1: number; x2: number; y2: number } | null {
  if (ref.kind === "office") {
    const o = layout.offices.find((x) => x.slotKey === ref.id);
    return o ? { x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2 } : null;
  }
  if (ref.kind === "zone") {
    const z = layout.commonZones.find((x) => x.id === ref.id);
    return z ? { x1: z.x1, y1: z.y1, x2: z.x2, y2: z.y2 } : null;
  }
  if (ref.kind === "furniture") {
    const f = layout.furniture.find((x) => x.id === ref.id);
    return f ? { x1: f.gx, y1: f.gy, x2: f.gx + f.w - 1, y2: f.gy + f.d - 1 } : null;
  }
  if (ref.kind === "transition") {
    const t = (layout.transitions ?? []).find((x) => x.id === ref.id);
    return t ? { x1: t.gx, y1: t.gy, x2: t.gx, y2: t.gy } : null;
  }
  const p = layout.spawnPoints[ref.index];
  return p ? { x1: p.gx, y1: p.gy, x2: p.gx, y2: p.gy } : null;
}
